export type NodeStatus = 'live' | 'coming-soon' | 'planned' | 'deprecated' | 'moving-to-frankie-brain' | 'under-construction'

export interface RoadmapNode {
  id: string
  label: string
  description: string
  /** lucide icon name */
  icon: string
  iconColor: string
  status: NodeStatus
  children?: RoadmapNode[]
}

export const roadmapTree: RoadmapNode = {
  id: 'dealhub',
  label: 'DealHub',
  description: 'Deal management platform for presales and implementations teams — deal workflows, quoting, scoping, SOWs, and AI-powered tools.',
  icon: 'LayoutDashboard',
  iconColor: 'text-blue-400',
  status: 'live',
  children: [
    {
      id: 'presales',
      label: 'Presales',
      description: 'Presales workspace — scoping, blueprints, dashboards, and a suite of tools to support the presales team from deal trigger to handover.',
      icon: 'Briefcase',
      iconColor: 'text-purple-400',
      status: 'live',
      children: [
        {
          id: 'scope',
          label: 'Scope',
          description: 'Configure implementation scope and requirements for each deal. Tracks completion across all configuration sections with enforced 100% completion before handover.',
          icon: 'ClipboardList',
          iconColor: 'text-blue-400',
          status: 'live',
          children: [
            {
              id: 'blueprint',
              label: 'Blueprint',
              description: 'Pre-sales discovery tool — MEDDPICC sales context from HubSpot, customer problem hypothesis, as-is architecture, discovery questions, current vendors, and cascade design.',
              icon: 'Map',
              iconColor: 'text-indigo-400',
              status: 'live',
              children: [
                {
                  id: 'cascade-builder',
                  label: 'Cascade Builder',
                  description: 'Visual KYC cascade designer — map out capture methods, validation vendors, rulesets, and decision outcomes per country on an interactive canvas.',
                  icon: 'Workflow',
                  iconColor: 'text-sky-400',
                  status: 'live',
                },
              ],
            },
            {
              id: 'order-form',
              label: 'Order Form',
              description: 'Schema-driven configuration form with sections for General, KYC, AML, ID Verification, KYB, Recipes/Workflows, Risk & Fraud, Hours, Actions, Custom Work, and Advanced settings.',
              icon: 'FileSpreadsheet',
              iconColor: 'text-emerald-400',
              status: 'live',
            },
          ],
        },
        {
          id: 'sow',
          label: 'Statement of Work',
          description: 'SOW builder with sections for Legal Text, Delivery Notes, Phases & Variation, Integration Sequence Diagram, RACI Chart, Gantt Chart, Key Assumptions, and Additional Notes. Sent via Annature for e-signature.',
          icon: 'FileText',
          iconColor: 'text-amber-400',
          status: 'live',
          children: [
            {
              id: 'sow-sequence',
              label: 'Integration Sequence Diagram',
              description: 'Visual diagram showing the end-to-end API flow between the customer\'s frontend, backend, FrankieOne, and data sources.',
              icon: 'GitBranch',
              iconColor: 'text-violet-400',
              status: 'live',
            },
            {
              id: 'sow-raci',
              label: 'RACI Chart',
              description: 'Responsibility assignment matrix — Responsible, Accountable, Consulted, Informed for each task in the implementation.',
              icon: 'Users',
              iconColor: 'text-orange-400',
              status: 'live',
            },
            {
              id: 'sow-gantt',
              label: 'Gantt Chart',
              description: 'Project timeline with tasks, responsible persons, party assignments (FrankieOne or Client), and start/end dates.',
              icon: 'BarChart3',
              iconColor: 'text-cyan-400',
              status: 'live',
            },
          ],
        },
        {
          id: 'dashboard',
          label: 'Dashboard',
          description: 'Presales performance tracking — KPIs (win ratio, cycle speed, demo capacity, deliverables), operational overview, actions, and calendar.',
          icon: 'BarChart3',
          iconColor: 'text-emerald-400',
          status: 'live',
          children: [
            {
              id: 'dashboard-performance',
              label: 'Performance (KPIs)',
              description: 'Quarterly KPI reporting — KR1 Win Ratio, KR2 Cycle Speed, KR3 Demo Capacity & Utilisation, KR4 Deliverables Completion. Broken down by presales engineer.',
              icon: 'Target',
              iconColor: 'text-red-400',
              status: 'live',
            },
            {
              id: 'dashboard-operations',
              label: 'Operational Overview',
              description: 'Real-time view of all deals in pre-handover and post-handover stages — deal actions progress, hours tracking, scope completion %, and deal stage.',
              icon: 'Activity',
              iconColor: 'text-green-400',
              status: 'live',
            },
            {
              id: 'dashboard-calendar',
              label: 'Calendar',
              description: 'Calendar tracker for demos and customer meetings — linked to deals via the Calendar Tracker extension for KR3 demo capacity measurement.',
              icon: 'CalendarDays',
              iconColor: 'text-purple-400',
              status: 'live',
            },
          ],
        },
        {
          id: 'tools',
          label: 'Tools',
          description: 'Suite of presales utilities — demo prep, RFP responses, match rate analysis, help doc generation, transcripts, POC management, and more.',
          icon: 'Wrench',
          iconColor: 'text-orange-400',
          status: 'live',
          children: [
            {
              id: 'tool-demo-prep',
              label: 'Demo Prep',
              description: 'AI-generated talking points for FrankieOne product demos — paste emails or notes, get a tailored demo plan with key discussion points.',
              icon: 'Presentation',
              iconColor: 'text-orange-400',
              status: 'live',
            },
            {
              id: 'tool-rfp',
              label: 'RFP Tool',
              description: 'Upload RFP/RFI spreadsheets, search reference docs with AI, assess fit, and manage response assignments across the team.',
              icon: 'FileCheck',
              iconColor: 'text-indigo-400',
              status: 'live',
            },
            {
              id: 'tool-match-rate',
              label: 'Match Rate Enquiry',
              description: 'AI-powered analysis of FrankieOne match rates — enter prospect requirements in natural language, get vendor recommendations per country.',
              icon: 'Search',
              iconColor: 'text-violet-400',
              status: 'live',
            },
            {
              id: 'tool-help-doc',
              label: 'Help Doc Builder',
              description: 'Generate customer-specific V1/V2 integration guides with toggleable features (KYC, IDV, KYB, AML, etc.) and PDF export.',
              icon: 'BookOpen',
              iconColor: 'text-blue-400',
              status: 'live',
            },
            {
              id: 'tool-transcripts',
              label: 'Transcripts',
              description: 'Centralised view of call transcripts and AI-condensed HubSpot activity excerpts for any deal.',
              icon: 'MessageSquareText',
              iconColor: 'text-cyan-400',
              status: 'live',
            },
            {
              id: 'tool-poc',
              label: 'POC Manager',
              description: 'Run proof-of-concept tests — vendor selection, batch execution tracking, and pass-rate cascade analysis.',
              icon: 'FlaskConical',
              iconColor: 'text-cyan-400',
              status: 'live',
            },
            {
              id: 'tool-process-flow',
              label: 'Process Flow',
              description: 'Visual flowchart of how Presales and Implementations work — from deal trigger to go-live, with scope lifecycle and KPIs.',
              icon: 'Route',
              iconColor: 'text-pink-400',
              status: 'live',
            },
            {
              id: 'tool-questions',
              label: 'Questions',
              description: 'Track prospect questions, KB gaps, and resolution status across deals.',
              icon: 'MessageCircleQuestion',
              iconColor: 'text-purple-400',
              status: 'live',
            },
            {
              id: 'tool-critical-path',
              label: 'Critical Path',
              description: 'RAG status, timelines, and risk tracking for deals approaching go-live.',
              icon: 'GitBranch',
              iconColor: 'text-amber-400',
              status: 'live',
            },
            {
              id: 'tool-pipelines-products',
              label: 'Pipelines & Products',
              description: 'Weekly agenda for the sales & products sync — upcoming scopes to review, custom work to discuss, and shared notes.',
              icon: 'Users',
              iconColor: 'text-emerald-400',
              status: 'live',
            },
            {
              id: 'tool-entity-delete',
              label: 'Entity Delete Tool',
              description: 'Bulk-delete entities from a FrankieOne environment using CSV or pasted IDs.',
              icon: 'Trash2',
              iconColor: 'text-red-400',
              status: 'live',
            },
            {
              id: 'tool-data-sources',
              label: 'Data Sources',
              description: 'Registry of source documents and spreadsheets that power DealHub tools — track versions, ownership, and quarterly refresh status.',
              icon: 'Database',
              iconColor: 'text-teal-400',
              status: 'live',
            },
            {
              id: 'tool-risk-profile',
              label: 'Risk Profile',
              description: 'Document and configure customer risk profiles — risk appetite, risk factors, and scoring models for implementation handover.',
              icon: 'ShieldCheck',
              iconColor: 'text-rose-400',
              status: 'live',
            },
          ],
        },
      ],
    },
    {
      id: 'implementations',
      label: 'Implementations',
      description: 'Implementations workspace — SOW template management, in-flight project tracking, and sequence diagram tools.',
      icon: 'Rocket',
      iconColor: 'text-emerald-400',
      status: 'live',
      children: [
        {
          id: 'impl-sow-templates',
          label: 'SOW Templates',
          description: 'Global SOW template editor — manage default legal text, delivery notes, RACI chart, Gantt chart, and key assumptions that new scopes inherit from.',
          icon: 'FileText',
          iconColor: 'text-amber-400',
          status: 'live',
        },
        {
          id: 'impl-reporting',
          label: 'Reporting Dashboard',
          description: 'In-flight implementation tracking — RAG status, duration monitoring, size categorisation, and commentary for all active implementations by quarter.',
          icon: 'BarChart3',
          iconColor: 'text-blue-400',
          status: 'live',
        },
        {
          id: 'impl-sequence-diagrams',
          label: 'Sequence Diagrams',
          description: 'Integration sequence diagram library — create, edit, and reuse diagrams showing API flows between customer systems, FrankieOne, and data sources.',
          icon: 'GitBranch',
          iconColor: 'text-violet-400',
          status: 'live',
        },
      ],
    },
  ],
}
